/**
 * The four tutorial scripts (docs/design/tutorial.md). Each names the class's real abilities,
 * keys and procs (packages/content/src/classes/*.ts, docs/design/how-to-play.md):
 *
 *   all      Move → (target the dummy | healer: target the tank)
 *   dummy    builder + spender → a proc ("press it now") → burst ("pop Q, then spend")
 *   golem    Dodge (Shift) out of the orange → finish it with the party
 *   pack     kick the Adept's Venom Bolt (healer: Cleanse its poison; tank: also Taunt)
 *   last     graduation pull, everything unlocked, rotation scored
 *
 * The Lightbinder heals the tank on the golem instead of hitting the dummy.
 */
import type { ContentRegistry } from '@keystone/sim';
import { ABILITY, AURA, CLASS, ENEMY_AURA } from '@keystone/content';
import { type BotMode, type Lock, type StepDef, type StepState, type TutFacts, type TutorialScript, type Unlock, allDead, anyEngaged, auraStacks, dist, hasAura, res } from './director';

type Content = Pick<ContentRegistry, 'ability' | 'class'>;

const cast = (s: StepState, id: string): number => s.n[`cast:${id}`] ?? 0;
const ALL_BINDS: Lock[] = ['1', '2', '3', '4', '5', '6', '7', 'Q', 'E', 'R', 'F', 'G'];

/** Every bind that has an ability for this class (plus Dodge). */
function locksOf(content: Content, classId: string): Lock[] {
  const c = content.class(classId);
  if (!c) return ALL_BINDS;
  const has = (b: Lock): boolean => {
    if (b === '5' || b === '6' || b === '7') return !!c.extraBinds?.[b];
    if (b === 'G') return c.extraBinds ? !!c.extraBinds.G : !!c.extraAbilities?.[0];
    return !!c.binds[b as keyof typeof c.binds];
  };
  return ALL_BINDS.filter(has);
}

function unlockMaker(content: Content) {
  return (lock: Lock, abilityId: string, why: string): Unlock => ({
    lock,
    abilityId,
    name: content.ability(abilityId)?.name ?? abilityId,
    key: lock === 'dodge' ? 'Shift' : lock,
    why,
  });
}

// ---------------------------------------------------------------------------------------------
// Shared steps
// ---------------------------------------------------------------------------------------------

const moveStep = (spot: { x: number; y: number }): StepDef => ({
  id: 'move',
  title: 'Move',
  text: 'Run with [W], backpedal with [S], strafe with [A] [D]. Hold the right mouse button and drag to turn your hero. Walk to the gold marker.',
  marker: 'spot',
  bots: 'hold',
  done: (_s, f) => !!f.me && dist(f.me.pos, spot) <= 4,
});

const targetStep: StepDef = {
  id: 'target',
  title: 'Target',
  text: 'Press [Tab] or click the Tutorial Dummy to target it.',
  marker: 'dummy',
  bots: 'hold',
  done: (_s, f) => !!f.dummy && f.selectedId === f.dummy.id,
};

/** "Press X now" while an aura that lights a button is up; tracks that it was consumed after lighting. */
function procHint(s: StepState, f: TutFacts, auraId: string, line: string): string | null {
  return hasAura(f.me, auraId) && !s.flag['used'] ? line : null;
}

/** After this many orange hits in the Dodge lesson the party comes to help anyway (the lesson stays). */
const DODGE_HELP_AFTER = 2;

function dodgeStep(text: string, extra: Partial<StepDef> = {}, alone: BotMode = 'hold'): StepDef {
  return {
    id: 'dodge',
    title: 'Dodge',
    text,
    hint: (s) => ((s.n['teleHit'] ?? 0) >= 1 ? 'That one hit you. Next time press [Shift] as soon as the orange appears, walking out of it.' : null),
    marker: 'golem',
    focus: 'dodge',
    bots: (_f, s) => ((s.n['teleHit'] ?? 0) >= DODGE_HELP_AFTER ? 'free' : alone),
    done: (s) => (s.n['dodged'] ?? 0) >= 1,
    // The golem died first (healer flow): the pack's Acid Spew teaches it instead.
    abortIf: (f) => !!f.golem && !f.golem.alive,
    ...extra,
  };
}

const finishGolemStep = (text: string): StepDef => ({
  id: 'golem',
  title: 'Finish the golem',
  text,
  marker: 'golem',
  bots: 'free',
  done: (_s, f) => !f.golem || !f.golem.alive,
});

function packStep(o: { text: string; goals: StepDef['goals']; hint?: StepDef['hint']; unlock: Unlock[]; holdKicks: StepDef['holdKicks'] }): StepDef {
  return {
    id: 'pack',
    title: 'The pack',
    text: o.text,
    hint: o.hint,
    unlock: o.unlock,
    marker: (_s, f) => (anyEngaged(f.lesson) ? null : 'lesson'),
    bots: 'free',
    holdKicks: o.holdKicks,
    goals: [...(o.goals ?? []), { label: 'Defeat the pack', done: (_s, f) => allDead(f.lesson) }],
    done: (_s, f) => allDead(f.lesson),
  };
}

const gradStep = (text: string, unlock: Unlock[]): StepDef => ({
  id: 'graduation',
  title: 'Graduation',
  text,
  unlock,
  marker: (_s, f) => (anyEngaged(f.graduation) ? null : 'graduation'),
  bots: 'free',
  scored: true,
  goals: [{ label: 'Defeat the last pack', done: (_s, f) => allDead(f.graduation) }],
  done: (_s, f) => allDead(f.graduation),
});

/** An enemy in the lesson pack is casting something the player can kick. */
const kickable = (f: TutFacts): boolean => f.lesson.some((u) => u.alive && !!u.cast?.interruptible);
const kickHint = (key: string, name: string) => (s: StepState, f: TutFacts): string | null =>
  !s.n['kick'] && kickable(f) ? `The Adept is casting! Target it and press [${key}] ${name} now.` : null;
const kickGoal = (key: string) => ({ label: `Kick a cast with [${key}]`, done: (s: StepState) => (s.n['kick'] ?? 0) >= 1 });

const jumps: NonNullable<TutorialScript['jumps']> = [
  // The lesson pack got pulled early (walked into it): skip ahead, everything up to it unlocked.
  { to: 'pack', when: (f) => anyEngaged(f.lesson) },
  { to: 'graduation', when: (f) => anyEngaged(f.graduation) },
];

// ---------------------------------------------------------------------------------------------
// Warden (tank)
// ---------------------------------------------------------------------------------------------

function warden(c: Content, spot: { x: number; y: number }): TutorialScript {
  const u = unlockMaker(c);
  const A = ABILITY.warden;
  return {
    classId: CLASS.warden,
    role: 'tank',
    allLocks: locksOf(c, CLASS.warden),
    jumps,
    steps: [
      moveStep(spot),
      targetStep,
      {
        id: 'builder',
        title: 'Build Resolve',
        text: 'Walk up to the dummy. Press [1] Shield Slam, then [2] Cleave Strike while Slam recharges, until you have 25 Resolve.',
        unlock: [u('1', A.shieldSlam, 'Builder: +15 Resolve and 1 Bastion. Press it whenever it is ready.'), u('2', A.cleaveStrike, 'Filler: +5 Resolve and a chance at Opening. Press it while Shield Slam recharges.')],
        focus: (s) => (cast(s, A.shieldSlam) ? '2' : '1'),
        marker: 'dummy',
        done: (s, f) => cast(s, A.shieldSlam) >= 1 && res(f.me, 'resolve') >= 25,
      },
      {
        id: 'spender',
        title: 'Spend it',
        text: 'Spend it: press [3] Bulwark Strike. It hits, heals you, and cashes in your Bastion.',
        hint: (_s, f) => (res(f.me, 'resolve') < 25 ? 'Bulwark Strike costs 25 Resolve: [1] Shield Slam or [2] Cleave Strike first.' : null),
        unlock: [u('3', A.bulwarkStrike, 'Spender (25 Resolve): a hit that heals you. More Bastion, bigger heal.')],
        focus: '3',
        done: (s) => cast(s, A.bulwarkStrike) >= 1,
      },
      {
        id: 'proc',
        title: 'Procs',
        text: 'Press [4] Consecrated Ground. It always grants Opening, which lights up Riposte.',
        hint: (s, f) =>
          procHint(s, f, AURA.opening, res(f.me, 'resolve') < 20 ? 'Riposte is lit but needs 20 Resolve: [1] or [2] first, then [6].' : 'Riposte is lit with the gold border. Press [6] now!'),
        unlock: [
          u('4', A.consecratedGround, 'Holy ground for 12 s: damages enemies, you take 10% less inside. Always grants Opening.'),
          u('6', A.riposte, 'Spender (20 Resolve): a frontal cone. With Opening it hits 75% harder and adds Bastion.'),
        ],
        focus: (_s, f) => (hasAura(f.me, AURA.opening) ? '6' : '4'),
        on: (e, s) => {
          if (e.type === 'removebuff' && e.abilityGameID === AURA.opening && e.reason === 'consumed') s.flag['used'] = true;
        },
        done: (s) => !!s.flag['used'] || (cast(s, A.riposte) >= 1 && (s.n[`gain:${AURA.opening}`] ?? 0) >= 1),
      },
      {
        id: 'transform',
        title: 'Transforms',
        text: 'Press [5] Aegis Throw. For 6 s the button turns into Aegis Recall: press [5] again to catch the shield.',
        hint: (_s, f) => (hasAura(f.me, AURA.aegisRecallReady) ? 'Key 5 is now Aegis Recall. Press [5] to catch your shield!' : null),
        unlock: [u('5', A.aegisThrow, 'Ranged shield toss: bounces to 3 enemies and interrupts. Then 5 becomes Aegis Recall for 6 s.')],
        focus: '5',
        done: (s) => cast(s, A.aegisRecall) >= 1,
      },
      {
        id: 'burst',
        title: 'Burst',
        text: 'Pop [Q] Last Stand: it sets Bastion to 6. Then cash it in with [3] Bulwark Strike.',
        unlock: [u('Q', A.lastStand, 'Major cooldown: 40% less damage for 8 s, +30 Resolve, and Bastion jumps to 6.')],
        focus: (s) => (s.flag['q'] ? '3' : 'Q'),
        on: (e, s) => {
          if (e.type !== 'cast') return;
          if (e.abilityGameID === A.lastStand) s.flag['q'] = true;
          if (e.abilityGameID === A.bulwarkStrike && s.flag['q']) s.flag['done'] = true;
        },
        done: (s) => !!s.flag['done'],
      },
      dodgeStep('Pull the Sparring Golem: [Tab] to it, then [5] Aegis Throw or walk in with [1] Shield Slam. When orange appears, press [Shift] to dash out.', {
        unlock: [u('dodge', ABILITY.common.dodge, 'Dash a few yards the way you are moving. Use it to get out of orange.')],
      }),
      finishGolemStep('Nice dodge! Your party is on the way. Hold the golem’s attention and finish it.'),
      packStep({
        text: 'Pull the pack with [5] Aegis Throw. Kick the Adept’s Venom Bolt with [R]. If a mob turns to a party member, [F] Taunt it back.',
        hint: (s, f) => {
          const k = kickHint('R', 'Shield Bash')(s, f);
          if (k) return k;
          const loose = f.lesson.find((m) => m.alive && m.inCombat && m.targetId && m.targetId !== f.me?.id && f.party.some((p) => p.id === m.targetId));
          return loose && !s.n[`cast:${A.taunt}`] ? `${loose.name} is attacking ${f.world.units[loose.targetId!]?.name ?? 'an ally'}: target it and press [F] Taunt.` : null;
        },
        unlock: [
          u('R', A.shieldBash, 'Interrupt: stops a cast and locks that school for 4 s.'),
          u('F', A.taunt, 'Forces an enemy to attack you for 3 s. Use it when a mob turns to a party member.'),
        ],
        goals: [kickGoal('R'), { label: 'Taunt with [F]', done: (s) => cast(s, A.taunt) >= 1 }],
        holdKicks: (s) => !s.n['kick'],
      }),
      gradStep('Graduation: every button is unlocked. Pull the last pack and use your whole kit. Face frontals away from the party.', [
        u('E', A.shieldWallBash, 'Off the GCD: 30% less physical damage for 6 s and Shield Slam hits harder. 2 charges.'),
        u('G', A.grip, 'Pull an enemy to you and taunt it. Great for casters that won’t come to you.'),
      ]),
    ],
  };
}

// ---------------------------------------------------------------------------------------------
// Lightbinder (healer)
// ---------------------------------------------------------------------------------------------

function lightbinder(c: Content, spot: { x: number; y: number }): TutorialScript {
  const u = unlockMaker(c);
  const A = ABILITY.lightbinder;
  const tankHealed = (s: StepState, f: TutFacts, id: string): boolean => !!f.tank && (s.n[`heal:${f.tank.id}:${id}`] ?? 0) >= 1;
  // The tank bot comes over only while the golem fights (otherwise it would go pull the pack).
  const tankWhileGolem = (f: TutFacts) => (f.golemEngaged ? 'tank' : 'hold') as 'tank' | 'hold';
  const golemGone = (f: TutFacts): boolean => !!f.golem && !f.golem.alive;
  return {
    classId: CLASS.lightbinder,
    role: 'healer',
    allLocks: locksOf(c, CLASS.lightbinder),
    jumps: [{ to: 'pack', when: (f) => anyEngaged(f.lesson) || golemGone(f) }, ...jumps.slice(1)],
    steps: [
      moveStep(spot),
      {
        id: 'party',
        title: 'Party frames',
        text: 'Your party frames show everyone’s health. Press [F1] or click the tank’s frame to target your tank.',
        focus: 'party',
        bots: 'hold',
        done: (_s, f) => !!f.tank && f.selectedId === f.tank.id,
      },
      {
        id: 'engage',
        title: 'Start the fight',
        text: 'Press [Tab] to target the Sparring Golem, then [4] Judgment. Your tank will run in and take it.',
        unlock: [u('4', A.judgment, 'Damage filler: +1 Holy Power and Holy Shock recharges sooner. Can grant Infusion of Light.')],
        focus: '4',
        marker: 'golem',
        bots: tankWhileGolem,
        done: (_s, f) => f.golemEngaged && !!f.tank && f.golem?.targetId === f.tank.id,
      },
      {
        id: 'heal',
        title: 'Heal the tank',
        text: 'Your tank is taking hits. Target them with [F1] and press [1] Flash Heal.',
        unlock: [u('1', A.flashHeal, 'Single-target heal with a short cast. Instant with Infusion of Light.')],
        focus: '1',
        marker: 'tank',
        bots: tankWhileGolem,
        done: (s, f) => tankHealed(s, f, A.flashHeal),
      },
      {
        id: 'builder',
        title: 'Holy Power',
        text: 'Build Holy Power with [2] Holy Shock (on the tank) and [4] Judgment (on the golem). At 3, spend it on [3] Word of Light.',
        hint: (_s, f) => (res(f.me, 'holy_power') >= 3 ? 'You have 3 Holy Power: target the tank and press [3] Word of Light.' : null),
        unlock: [u('2', A.holyShock, 'Instant heal or damage, 2 charges: +1 Holy Power.'), u('3', A.wordOfLight, 'Spender: 3 Holy Power for a big instant heal.')],
        focus: (_s, f) => (res(f.me, 'holy_power') >= 3 ? '3' : '2'),
        bots: tankWhileGolem,
        done: (s) => cast(s, A.wordOfLight) >= 1,
      },
      {
        id: 'proc',
        title: 'Procs',
        text: 'Keep using [2] Holy Shock and [4] Judgment until Flash Heal lights up with a marching gold border.',
        hint: (s, f) => procHint(s, f, AURA.infusionOfLight, 'Infusion of Light! [1] Flash Heal is instant now: press it on the tank.'),
        focus: (_s, f) => (hasAura(f.me, AURA.infusionOfLight) ? '1' : '2'),
        bots: tankWhileGolem,
        on: (e, s) => {
          if (e.type === 'removebuff' && e.abilityGameID === AURA.infusionOfLight && e.reason === 'consumed') s.flag['used'] = true;
        },
        done: (s) => !!s.flag['used'],
      },
      {
        id: 'burst',
        title: 'Burst',
        text: 'Press [6] Dawnbringer: a big heal and 15 s of Avenging Light. Spend with [3] Word of Light to build Dawnlight, then press [6] again for Sunfall.',
        hint: (s, f) => {
          if (!s.flag['db']) return null;
          if (auraStacks(f.me, AURA.dawnlight) >= 1) return 'Key 6 is now Sunfall. Press [6] to cash in your Dawnlight!';
          return res(f.me, 'holy_power') >= 3 ? 'Spend Holy Power on [3] Word of Light to build Dawnlight.' : 'Build Holy Power with [2] Holy Shock, then [3] Word of Light.';
        },
        unlock: [
          u('6', A.dawnbringer, '60 s cooldown: heals 5 allies, +3 Holy Power, and opens Avenging Light. Key 6 becomes Sunfall.'),
          u('Q', A.radiantWave, 'Heals everyone near you plus a heal over time, and grants Infusion of Light.'),
        ],
        focus: (s, f) => (!s.flag['db'] ? '6' : auraStacks(f.me, AURA.dawnlight) >= 1 ? '6' : '3'),
        bots: tankWhileGolem,
        on: (e, s) => {
          if (e.type === 'cast' && e.abilityGameID === A.dawnbringer) s.flag['db'] = true;
          if (e.type === 'cast' && e.abilityGameID === A.sunfall) s.flag['done'] = true;
          // The window closed without a Sunfall: move on (the summary mentions it).
          if (e.type === 'removebuff' && e.abilityGameID === AURA.sunfallReady && e.reason === 'expired') s.flag['done'] = true;
        },
        done: (s) => !!s.flag['done'],
      },
      dodgeStep(
        'Orange ring under you? Press [Shift] to dash out. Your Dodge goes the way you are walking.',
        { unlock: [u('dodge', ABILITY.common.dodge, 'Dash a few yards the way you are moving. Use it to get out of orange.')] },
        'tank',
      ),
      finishGolemStep('Nice! The rest of the party joins in. Help finish the golem with [4] Judgment and [2] Holy Shock.'),
      packStep({
        text: 'Follow your tank. The Adept’s Venom Bolt poisons an ally: target them ([F1]–[F4] or click) and press [R] Cleanse.',
        hint: (s, f) => {
          if (s.n['dispel']) return null;
          const p = f.party.find((m) => m.alive && m.auras.some((a) => POISONS.has(a.auraId)));
          return p ? `${p.id === f.me?.id ? 'You are' : `${p.name} is`} poisoned! Target ${p.id === f.me?.id ? 'yourself' : 'them'} and press [R] Cleanse.` : null;
        },
        unlock: [
          u('R', A.cleanse, 'Dispel: removes one Poison or Magic effect from an ally.'),
          u('5', A.dawnsong, 'Spender: 3 Holy Power heals the 5 most injured allies. Use it when damage spreads.'),
          u('E', A.guardiansAegis, 'An ally takes 40% less damage for 8 s. Save it for big hits on the tank.'),
        ],
        goals: [{ label: 'Cleanse a poison with [R]', done: (s) => (s.n['dispel'] ?? 0) >= 1 }],
        holdKicks: (s) => !s.n['dispel'],
      }),
      gradStep('Graduation: every button is unlocked. Keep the party alive through the last pull, and Cleanse poisons.', [
        u('F', A.divineShield, 'Immune to all damage for 8 s. Your emergency button.'),
        u('G', A.blessingOfFreedom, 'An ally is immune to control effects for 6 s.'),
      ]),
    ],
  };
}

/** Poisons the Lightbinder's Cleanse removes in the tutorial packs (Venom Bolt, Acid Spew). */
const POISONS = new Set<string>([ENEMY_AURA.envenom, ENEMY_AURA.acidBurn]);

// ---------------------------------------------------------------------------------------------
// Blademaster (melee)
// ---------------------------------------------------------------------------------------------

function blademaster(c: Content, spot: { x: number; y: number }): TutorialScript {
  const u = unlockMaker(c);
  const A = ABILITY.blademaster;
  return {
    classId: CLASS.blademaster,
    role: 'melee',
    allLocks: locksOf(c, CLASS.blademaster),
    jumps,
    steps: [
      moveStep(spot),
      targetStep,
      {
        id: 'builder',
        title: 'Build Wounds',
        text: 'Walk into melee range and press [2] Overpower twice. Each hit puts a Wounds stack on the dummy.',
        unlock: [u('2', A.overpower, 'Builder with 2 charges: +12 Rage and 1 Wounds on the target.')],
        focus: '2',
        marker: 'dummy',
        done: (s) => cast(s, A.overpower) >= 2,
      },
      {
        id: 'spender',
        title: 'Spend it',
        text: 'Cash in the Wounds: press [1] Mortal Strike. It hits 10% harder per stack.',
        hint: (_s, f) => (res(f.me, 'rage') < 30 ? 'Mortal Strike needs 30 Rage. Auto-attacks and [2] Overpower build it.' : null),
        unlock: [u('1', A.mortalStrike, 'Main spender (30 Rage): +10% damage per Wounds stack, then consumes them.')],
        focus: '1',
        done: (s) => cast(s, A.mortalStrike) >= 1,
      },
      {
        id: 'proc',
        title: 'Procs',
        text: 'Keep going with [2] Overpower, [5] Slam and [1] Mortal Strike until a button lights up with a marching gold border.',
        hint: (s, f) =>
          procHint(s, f, AURA.suddenDeath, 'Sudden Death! [4] Execute is lit and free: press it now!') ??
          procHint(s, f, AURA.overpowerProc, 'Tactician! [2] Overpower is lit: press it now for 50% more damage.'),
        unlock: [
          u('5', A.slam, 'Cheap filler: 1 Wounds, and a chance at Sudden Death or Tactician.'),
          u('4', A.execute, 'Finisher: below 20% health, or anytime with Sudden Death (then it is free).'),
        ],
        focus: (_s, f) => (hasAura(f.me, AURA.suddenDeath) ? '4' : hasAura(f.me, AURA.overpowerProc) ? '2' : '5'),
        on: (e, s) => {
          if (e.type === 'removebuff' && (e.abilityGameID === AURA.suddenDeath || e.abilityGameID === AURA.overpowerProc) && e.reason === 'consumed') s.flag['used'] = true;
          if (e.type === 'removebuffstack' && e.abilityGameID === AURA.suddenDeath) s.flag['used'] = true;
        },
        done: (s) => !!s.flag['used'],
      },
      {
        id: 'burst',
        title: 'Burst',
        text: 'Pop [E] Colossus Smash, then [Q] Bladestorm. When the spin ends, Q becomes Ravager’s Finale: press [Q] again.',
        hint: (_s, f) => (hasAura(f.me, AURA.ravagersFinaleReady) ? 'Q is now Ravager’s Finale. Press [Q] to cash in all those Wounds!' : null),
        unlock: [
          u('E', A.colossusSmash, '30 s window: +20% damage for 8 s, 2 Wounds and an Overpower charge.'),
          u('Q', A.bladestorm, '8 s spin. When it ends, Q becomes Ravager’s Finale for 10 s.'),
        ],
        focus: (s, f) => (hasAura(f.me, AURA.ravagersFinaleReady) ? 'Q' : cast(s, A.colossusSmash) ? 'Q' : 'E'),
        done: (s) => cast(s, A.ravagersFinale) >= 1,
      },
      dodgeStep('Press [G] Charge to jump into the Sparring Golem. When orange appears, press [Shift] to dash out of it.', {
        unlock: [
          u('G', A.charge, 'Charge to an enemy: +15 Rage. Your opener.'),
          u('dodge', ABILITY.common.dodge, 'Dash a few yards the way you are moving. Use it to get out of orange.'),
        ],
      }),
      finishGolemStep('Nice dodge! Your party joins in. Finish the golem.'),
      packStep({
        text: 'Follow your tank to the pack. Kick the Adept’s Venom Bolt with [R] Pummel, and step out of the Serpent’s spit.',
        hint: kickHint('R', 'Pummel'),
        unlock: [
          u('R', A.pummel, 'Interrupt: stops a cast and locks its school for 4 s.'),
          u('3', A.cleave, 'Pack builder: hits up to 5, 1 Wounds each. Your next Mortal Strike cleaves.'),
        ],
        goals: [kickGoal('R')],
        holdKicks: (s) => !s.n['kick'],
      }),
      gradStep('Graduation: every button is unlocked. Pull the last pack: [Q] Bladestorm, then cash in, and keep kicking.', [
        u('6', A.rend, '18 s bleed. Refresh it in its last 30%. Its ticks can grant Sudden Death.'),
        u('F', A.dieByTheSword, '30% less damage taken and parry frontal melee for 8 s.'),
      ]),
    ],
  };
}

// ---------------------------------------------------------------------------------------------
// Stormcaller (ranged)
// ---------------------------------------------------------------------------------------------

function stormcaller(c: Content, spot: { x: number; y: number }): TutorialScript {
  const u = unlockMaker(c);
  const A = ABILITY.stormcaller;
  return {
    classId: CLASS.stormcaller,
    role: 'ranged',
    allLocks: locksOf(c, CLASS.stormcaller),
    jumps,
    steps: [
      moveStep(spot),
      targetStep,
      {
        id: 'builder',
        title: 'Build Maelstrom',
        text: 'Stand still and cast [1] Lightning Bolt and [2] Lava Burst until you have 50 Maelstrom.',
        unlock: [u('1', A.lightningBolt, 'Filler cast: +8 Maelstrom and 1 Static Charge.'), u('2', A.lavaBurst, '+10 Maelstrom, 2 charges. Always crits on a Flame Shocked target.')],
        focus: (_s, f) => (f.me && (f.me.resources['maelstrom']?.cur ?? 0) >= 50 ? null : '1'),
        marker: 'dummy',
        done: (_s, f) => res(f.me, 'maelstrom') >= 50,
      },
      {
        id: 'spender',
        title: 'Spend it',
        text: 'Spend it: press [5] Earth Shock. It is instant and cashes in your Static Charge.',
        unlock: [u('5', A.earthShock, 'Spender: 50 Maelstrom, instant. +8% damage per Static Charge stack.')],
        focus: '5',
        done: (s) => cast(s, A.earthShock) >= 1,
      },
      {
        id: 'proc',
        title: 'Procs',
        text: 'Press [E] Flame Shock, then channel [6] Arc Torrent. Stand still until it finishes: that always grants Lava Surge.',
        hint: (s, f) => procHint(s, f, AURA.lavaSurge, 'Lava Surge! [2] Lava Burst is lit and instant: press it now.'),
        unlock: [
          u('E', A.flameShock, '18 s burn. Lava Burst always crits on it, and its ticks can grant Lava Surge.'),
          u('6', A.arcTorrent, '2.5 s channel. Finish it for a Lava Surge and 5 Static Charge.'),
        ],
        focus: (s, f) => (hasAura(f.me, AURA.lavaSurge) ? '2' : cast(s, A.flameShock) ? '6' : 'E'),
        on: (e, s) => {
          if ((e.type === 'removebuff' || e.type === 'removebuffstack') && e.abilityGameID === AURA.lavaSurge && (e.type === 'removebuffstack' || e.reason === 'consumed')) s.flag['used'] = true;
        },
        done: (s) => !!s.flag['used'],
      },
      {
        id: 'burst',
        title: 'Burst',
        text: 'Pop [Q] Stormkeeper, then fire [1] Lightning Bolt three times. They are instant and hit 50% harder.',
        unlock: [u('Q', A.stormkeeper, 'Off the GCD: your next 3 Lightning Bolts are instant and deal 50% more.')],
        focus: (s) => (s.flag['q'] ? '1' : 'Q'),
        on: (e, s) => {
          if (e.type !== 'cast') return;
          if (e.abilityGameID === A.stormkeeper) s.flag['q'] = true;
          else if (s.flag['q'] && (e.abilityGameID === A.lightningBolt || e.abilityGameID === A.tempest)) s.n['bolts'] = (s.n['bolts'] ?? 0) + 1;
        },
        done: (s) => (s.n['bolts'] ?? 0) >= 3,
      },
      dodgeStep('Target the Sparring Golem and hit it. It walks up to you: when orange appears, press [Shift] to dash out.', {
        unlock: [u('dodge', ABILITY.common.dodge, 'Dash a few yards the way you are moving. Use it to get out of orange.')],
      }),
      finishGolemStep('Nice dodge! Your party joins in. Finish the golem.'),
      packStep({
        text: 'Follow your tank to the pack. Kick the Adept’s Venom Bolt with [R] Wind Shear, and step out of the Serpent’s spit.',
        hint: kickHint('R', 'Wind Shear'),
        unlock: [
          u('R', A.windShear, 'Interrupt: stops a cast and locks its school for 3 s.'),
          u('3', A.chainLightning, 'Hits 3 enemies: your filler on packs.'),
          u('4', A.earthquake, 'Pack spender (60 Maelstrom): shakes the ground for 6 s.'),
        ],
        goals: [kickGoal('R')],
        holdKicks: (s) => !s.n['kick'],
      }),
      gradStep('Graduation: every button is unlocked. Every third spender turns [1] into Tempest. Pull the last pack!', [
        u('F', A.astralShift, '40% less damage taken for 8 s.'),
      ]),
    ],
  };
}

const BUILDERS: Record<string, (c: Content, spot: { x: number; y: number }) => TutorialScript> = {
  [CLASS.warden]: warden,
  [CLASS.lightbinder]: lightbinder,
  [CLASS.blademaster]: blademaster,
  [CLASS.stormcaller]: stormcaller,
};

export const TUTORIAL_CLASSES: readonly string[] = Object.keys(BUILDERS);

/** The script for a class (falls back to the Blademaster's for unknown ids). */
export function buildScript(classId: string, content: Content, spot: { x: number; y: number }): TutorialScript {
  return (BUILDERS[classId] ?? blademaster)(content, spot);
}
